import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface AccessDestination {
  id: string;
  name: string;
  location: string | null;
  security_level: string;
  icon: string;
  condominium_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useDestinations = () => {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["access_destinations", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return [];
      const { data, error } = await supabase
        .from("access_destinations")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .order("name");
      if (error) throw error;
      return data as AccessDestination[];
    },
    enabled: !!profile?.condominium_id,
  });
};

export const useActiveDestinations = () => {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["access_destinations_active", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return [];
      const { data, error } = await supabase
        .from("access_destinations")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data as AccessDestination[];
    },
    enabled: !!profile?.condominium_id,
  });
};

export const useCreateDestination = () => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (data: { name: string; location?: string; security_level?: string; icon?: string }) => {
      if (!profile?.condominium_id) throw new Error("Sem organização associada");
      const { data: dest, error } = await supabase
        .from("access_destinations")
        .insert({ ...data, condominium_id: profile.condominium_id })
        .select()
        .single();
      if (error) throw error;
      return dest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access_destinations"] });
      queryClient.invalidateQueries({ queryKey: ["access_destinations_active"] });
      toast.success("Destino criado com sucesso!");
    },
    onError: () => toast.error("Erro ao criar destino"),
  });
};

export const useUpdateDestination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; location?: string; security_level?: string; icon?: string; is_active?: boolean }) => {
      const { data: dest, error } = await supabase
        .from("access_destinations")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return dest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access_destinations"] });
      queryClient.invalidateQueries({ queryKey: ["access_destinations_active"] });
      toast.success("Destino atualizado!");
    },
    onError: () => toast.error("Erro ao atualizar destino"),
  });
};

export const useDeleteDestination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("access_destinations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access_destinations"] });
      queryClient.invalidateQueries({ queryKey: ["access_destinations_active"] });
      toast.success("Destino removido!");
    },
    onError: () => toast.error("Erro ao remover destino"),
  });
};
